import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getValuationPersonInputs } from '@dealstack/frontend/shared/dataExchange/services/valuations.service';
import { ValuationPersonInputRes } from '@dealstack/shared/lib/generated';
import { PersonInputFormValue } from '@dealstack/frontend/app/pages/Valuations/views/SourcesAndUses/types';
import { VALUATION_PERSON_INPUTS_QUERY_KEY } from '@dealstack/frontend/app/pages/Valuations/queries/queryKeys';
import {
	CATEGORY_TO_CLASSIFIER_MAP,
	PersonInputSavePayload,
} from '@dealstack/frontend/app/pages/Valuations/queries/personInputClassifiers';
import { usePersonInputsUpsert } from '@dealstack/frontend/app/pages/Valuations/queries/usePersonInputsUpsert';
import { usePersonInputDelete } from '@dealstack/frontend/app/pages/Valuations/queries/usePersonInputDelete';

export type { PersonInputSavePayload };

export function usePersonInputsQuery(
	dealId: string | undefined,
	valuationId: string | undefined,
	personId: string | undefined,
	mipId: string | undefined,
	reloadSummary: () => Promise<unknown>
) {
	const queryKey = useMemo(
		() => [
			VALUATION_PERSON_INPUTS_QUERY_KEY,
			dealId ?? '',
			valuationId ?? '',
			personId ?? '',
			mipId ?? '',
		],
		[dealId, valuationId, personId, mipId]
	);

	const inputsQuery = useQuery({
		queryKey,
		queryFn: () =>
			getValuationPersonInputs({
				valuationId: valuationId ?? '',
				dealId: dealId ?? '',
				personId: personId!,
				mipId,
			}),
		enabled: !!dealId && !!valuationId && !!personId,
	});

	const { saveFields, flush } = usePersonInputsUpsert({
		dealId,
		valuationId,
		personId,
		mipId,
		queryKey,
		reloadSummary,
	});

	const { deleteField } = usePersonInputDelete({
		dealId,
		valuationId,
		mipId,
		queryKey,
		reloadSummary,
	});

	const getPersonInputsByCategory = useCallback(
		(categoryId: string): PersonInputFormValue[] => {
			const inputs = inputsQuery.data?.payload;
			const classifierId = CATEGORY_TO_CLASSIFIER_MAP[categoryId] || categoryId;

			if (!inputs || !Array.isArray(inputs)) {
				return [];
			}

			return inputs
				.filter((input) => input.classifier.id === classifierId)
				.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
				.map((input) => ({
					name: input.name,
					value: {
						currencyAmount: input.currencyAmount.toString(),
						currency: input.currency,
					},
					personInputId: input.id,
					createdAt: input.createdAt,
				}));
		},
		[inputsQuery.data?.payload]
	);

	return {
		personInputs: inputsQuery.data?.payload as ValuationPersonInputRes[] | undefined,
		isLoading: inputsQuery.isLoading,
		saveFields,
		deleteField,
		flushPersonInput: flush,
		getPersonInputsByCategory,
	};
}
